import type { UserProfile } from '@/types/user';

/**
 * Returns the name that should be shown publicly for a user.
 *
 * Resolution order per preference:
 *  'nickname'    → nickname, fallback to displayName
 *  'realName'    → firstName + lastName, fallback to displayName
 *  'both'        → "Nickname (Real Name)", fallback to either alone
 *  'displayName' → displayName (default / backward-compat)
 *
 * The real firstName/lastName is kept private for system/payout use and is
 * never exposed to other users unless the user explicitly chooses 'realName' or 'both'.
 */
export function getPublicName(profile: UserProfile | null | undefined): string {
  if (!profile) return 'User';

  const nickname = profile.nickname?.trim() ?? '';
  const realName = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayName = profile.displayName?.trim() ?? '';
  const fallback = displayName || nickname || realName || profile.email || 'User';

  switch (profile.nameDisplayPreference) {
    case 'nickname':
      return nickname || fallback;
    case 'realName':
      return realName || fallback;
    case 'both':
      if (nickname && realName && nickname !== realName) {
        return `${nickname} (${realName})`;
      }
      return nickname || realName || fallback;
    case 'displayName':
    default:
      return fallback;
  }
}
